import React, { useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, AlertCircle, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StorageBar, StorageUpgradeModal, useStorageUsage } from "../components/Storage";

function formatDate(iso) {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    } catch {
        return iso;
    }
}

function daysUntil(iso) {
    if (!iso) return null;
    const ms = new Date(iso).getTime() - Date.now();
    return Math.max(0, Math.ceil(ms / (24 * 3600 * 1000)));
}

export default function Account() {
    const { user, logout, refresh } = useAuth();
    const navigate = useNavigate();
    const { usage, refresh: refreshStorage } = useStorageUsage();
    const [upgradeOpen, setUpgradeOpen] = useState(false);
    const [sub, setSub] = useState(null);
    const [busy, setBusy] = useState(null); // 'cancel' | 'resume' | null

    const loadSubscription = useCallback(async () => {
        try {
            const { data } = await api.get("/account/subscription");
            setSub(data);
        } catch { /* free user / unauth — ignore */ }
    }, []);

    useEffect(() => { if (user) loadSubscription(); }, [user, loadSubscription]);

    if (!user) return null;

    const handleLogout = async () => { await logout(); navigate("/"); };

    const handleCancel = async () => {
        if (!window.confirm("Cancel your subscription at period end? You'll keep Pro access until your renewal date.")) return;
        try {
            setBusy("cancel");
            await api.post("/account/cancel-subscription");
            toast.success("Subscription will end at the current period.");
            await loadSubscription();
            if (refresh) await refresh();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not cancel");
        } finally { setBusy(null); }
    };

    const handleResume = async () => {
        try {
            setBusy("resume");
            await api.post("/account/resume-subscription");
            toast.success("Subscription resumed.");
            await loadSubscription();
            if (refresh) await refresh();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not resume");
        } finally { setBusy(null); }
    };

    // Display labels for each plan/status combo.
    const planLabel = (() => {
        const status = sub?.subscription_status || user.subscription_status || "free";
        const plan = sub?.subscription_plan || user.subscription_plan;
        if (user.role === "admin") return "Admin (full access)";
        if (status === "lifetime") return "Lifetime (legacy $1 — grandfathered)";
        if (status === "active" && plan === "yearly") return "Pro · Yearly ($79/yr)";
        if (status === "active" && plan === "monthly") return "Pro · Monthly ($9.99/mo)";
        if (status === "active") return "Pro · Active";
        if (status === "trialing") return "Pro · 14-day trial";
        if (status === "past_due") return "Pro · Payment past due";
        if (status === "canceled") return "Canceled";
        return "Free viewer";
    })();

    const isPro = user.is_paid;
    const isTrial = sub?.subscription_status === "trialing" || (user.subscription_status === "trialing");
    const renewsAt = sub?.subscription_renews_at;
    const trialEndsAt = sub?.trial_ends_at;
    const cancelAtPeriodEnd = sub?.cancel_at_period_end;
    const daysLeftInTrial = isTrial ? daysUntil(trialEndsAt) : null;

    return (
        <>
            <Helmet><title>Account — OpenCSV</title></Helmet>
            <section className="mx-auto max-w-3xl px-4 md:px-8 py-16">
                <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted mb-3">/ account</div>
                <h1 className="font-display font-black tracking-tighter text-4xl">Hello, {user.name}.</h1>

                <div className="mt-8 grid md:grid-cols-2 gap-px bg-ink/10 border border-ink/10">
                    <div className="bg-white p-6">
                        <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted">Email</div>
                        <div className="mt-1 font-mono text-sm">{user.email}</div>
                    </div>
                    <div className="bg-white p-6">
                        <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted">Sign-in method</div>
                        <div className="mt-1 font-mono text-sm capitalize">{user.auth_provider}</div>
                    </div>
                    <div className="bg-white p-6">
                        <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted">Plan</div>
                        <div className="mt-1 font-mono text-sm" data-testid="account-plan-label">{planLabel}</div>
                    </div>
                    <div className="bg-white p-6">
                        <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted">Role</div>
                        <div className="mt-1 font-mono text-sm capitalize">{user.role}</div>
                    </div>
                </div>

                {/* Subscription card */}
                <div className="mt-8 border border-ink/15 bg-white p-6" data-testid="account-subscription-card">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                            <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted">Subscription</div>
                            <h2 className="mt-1 font-display font-black text-2xl tracking-tighter">{planLabel}</h2>
                            {isTrial && trialEndsAt && (
                                <p className="mt-2 text-sm text-cobalt inline-flex items-center gap-1.5">
                                    <Sparkles size={13} /> {daysLeftInTrial} day{daysLeftInTrial === 1 ? "" : "s"} left in your trial — ends {formatDate(trialEndsAt)}
                                </p>
                            )}
                            {!isTrial && renewsAt && sub?.subscription_status === "active" && !cancelAtPeriodEnd && (
                                <p className="mt-2 text-sm text-ink-soft">Renews on {formatDate(renewsAt)}</p>
                            )}
                            {cancelAtPeriodEnd && renewsAt && (
                                <p className="mt-2 text-sm text-signal-orange inline-flex items-center gap-1.5">
                                    <AlertCircle size={13} /> Set to cancel — Pro access ends {formatDate(renewsAt)}
                                </p>
                            )}
                            {sub?.subscription_status === "past_due" && (
                                <p className="mt-2 text-sm text-signal-red inline-flex items-center gap-1.5">
                                    <AlertCircle size={13} /> Payment failed. Update your card to keep Pro access.
                                </p>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {!isPro && (
                                <Link to="/pricing" className="btn-brutal bg-cobalt text-white border border-cobalt px-4 py-2 ui-heading text-sm font-semibold inline-flex items-center gap-1.5" data-testid="account-subscribe">
                                    <Sparkles size={13} /> Upgrade to Pro
                                </Link>
                            )}
                            {isTrial && (
                                <Link to="/pricing" className="btn-brutal bg-cobalt text-white border border-cobalt px-4 py-2 ui-heading text-sm font-semibold" data-testid="account-finish-trial">
                                    Subscribe before trial ends
                                </Link>
                            )}
                            {sub?.subscription_status === "active" && !cancelAtPeriodEnd && sub?.stripe_subscription_id && (
                                <button onClick={handleCancel} disabled={busy === "cancel"} className="btn-brutal border border-signal-red/40 text-signal-red px-4 py-2 ui-heading text-sm disabled:opacity-50" data-testid="account-cancel-sub">
                                    {busy === "cancel" ? "Cancelling…" : "Cancel subscription"}
                                </button>
                            )}
                            {cancelAtPeriodEnd && sub?.stripe_subscription_id && (
                                <button onClick={handleResume} disabled={busy === "resume"} className="btn-brutal border border-signal-green/40 text-signal-green px-4 py-2 ui-heading text-sm inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="account-resume-sub">
                                    <CheckCircle size={13} /> {busy === "resume" ? "Resuming…" : "Resume subscription"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {isPro && (
                    <div className="mt-8 border border-ink/10 bg-white p-6" data-testid="account-storage-card">
                        <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
                            <div>
                                <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted">Cloud storage</div>
                                <h2 className="mt-1 font-display font-black text-2xl tracking-tighter">
                                    {usage ? `${usage.files_count} files saved` : "Loading…"}
                                </h2>
                            </div>
                            <button
                                onClick={() => setUpgradeOpen(true)}
                                className="btn-brutal bg-cobalt text-white px-4 py-2 ui-heading text-sm font-semibold border border-cobalt"
                                data-testid="account-storage-upgrade"
                            >
                                Upgrade storage →
                            </button>
                        </div>
                        <StorageBar usage={usage} />
                        <p className="mt-3 text-xs text-ink-muted">
                            Local open / edit / download is unlimited and never counts against your cloud quota.
                            Manage your saved files in the editor's <Link to="/app" className="underline">Cloud panel</Link>.
                        </p>
                    </div>
                )}

                <div className="mt-8 flex flex-wrap gap-3">
                    <Link to="/app" className="btn-brutal bg-ink text-white px-4 py-2.5 ui-heading text-sm font-semibold border border-ink" data-testid="account-open-editor">
                        Open editor
                    </Link>
                    <button onClick={handleLogout} className="btn-brutal border border-ink/20 px-4 py-2.5 ui-heading text-sm" data-testid="account-logout">
                        Sign out
                    </button>
                </div>
            </section>

            <StorageUpgradeModal
                open={upgradeOpen}
                onClose={() => { setUpgradeOpen(false); refreshStorage(); }}
                currentUsage={usage}
            />
        </>
    );
}
